import {
  useEffect,
  useRef,
  useState,
} from "react";

import { createPortal } from "react-dom";

import {
  api,
  getPosHeaders,
  savePosSession,
} from "./api";

import "./POSProfileModal.css";


function errorText(error) {
  const detail = error.response?.data?.detail;

  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => item.msg)
      .filter(Boolean)
      .join(". ");
  }

  return error.message || "No fue posible actualizar tus datos.";
}


function PasswordField({
  id,
  label,
  value,
  onChange,
  disabled,
  required = false,
  autoComplete,
}) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="pos-profile-field">
      <label htmlFor={id}>{label}</label>

      <div className="pos-profile-password">
        <input
          id={id}
          type={visible ? "text" : "password"}
          value={value}
          onChange={onChange}
          disabled={disabled}
          required={required}
          autoComplete={autoComplete}
        />

        <button
          type="button"
          onClick={() => setVisible((current) => !current)}
          disabled={disabled}
          aria-label={`${visible ? "Ocultar" : "Mostrar"} ${label}`}
          aria-pressed={visible}
          aria-controls={id}
        >
          {visible ? "Ocultar" : "Ver"}
        </button>
      </div>
    </div>
  );
}


export default function POSProfileModal({
  company,
  user,
  primary,
  onClose,
  onSaved,
}) {
  const [name, setName] = useState(user.name || "");
  const [email, setEmail] = useState(user.email || "");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submittingRef = useRef(false);
  const dialogRef = useRef(null);
  const nameRef = useRef(null);

  const wantsPasswordChange =
    Boolean(newPassword || confirmPassword);

  const passwordsMatch =
    newPassword === confirmPassword;

  const passwordBytes =
    new TextEncoder().encode(newPassword).length;

  const invalidNewPassword =
    wantsPasswordChange &&
    (
      !passwordsMatch ||
      newPassword.length < 8 ||
      passwordBytes > 72
    );

  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";
    nameRef.current?.focus();

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        event.preventDefault();

        if (!submittingRef.current) {
          onClose();
        }

        return;
      }

      if (event.key !== "Tab") return;

      const elements = Array.from(
        dialogRef.current?.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), [tabindex="0"]'
        ) || []
      );

      if (!elements.length) return;

      const first = elements[0];
      const last = elements[elements.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);

      if (previousFocus?.isConnected) {
        previousFocus.focus();
      }
    };
  }, [onClose]);

  async function handleSubmit(event) {
    event.preventDefault();

    if (submittingRef.current || invalidNewPassword) return;

    submittingRef.current = true;
    setSaving(true);
    setError("");

    let updatedSession;

    try {
      const { data } = await api.put(
        "/auth/pos/profile",
        {
          name: name.trim(),
          email: email.trim(),
          current_password: currentPassword,
          new_password: newPassword || null,
          confirm_password: confirmPassword || null,
        },
        {
          headers: getPosHeaders(company),
        }
      );

      updatedSession = data;
    } catch (requestError) {
      setError(errorText(requestError));
      submittingRef.current = false;
      setSaving(false);
      return;
    }

    try {
      // Guardar el token nuevo antes de hacer otras consultas.
      savePosSession(company, updatedSession);
    } catch {
      setError(
        "Tus datos se actualizaron en el servidor, pero el navegador " +
        "no pudo guardar la sesión actualizada. Mantén esta ventana " +
        "abierta y vuelve a intentar guardar."
      );

      // Conserva una forma de recuperar el guardado local
      // sin repetir la actualización con el token anterior.
      pendingSessionRef.current = updatedSession;

      submittingRef.current = false;
      setSaving(false);
      return;
    }

    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");

    submittingRef.current = false;
    setSaving(false);

    onSaved(updatedSession);
  }

  const pendingSessionRef = useRef(null);

  function recoverSession() {
    try {
      savePosSession(company, pendingSessionRef.current);
      onSaved(pendingSessionRef.current);
    } catch {
      setError(
        "El navegador sigue sin permitir guardar la sesión. " +
        "Revisa que el almacenamiento de la página esté habilitado."
      );
    }
  }

  return createPortal(
    <div
      className="pos-profile-backdrop"
      style={{
        "--profile-primary": primary || "#334155",
      }}
    >
      <section
        ref={dialogRef}
        className="pos-profile-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pos-profile-title"
        aria-describedby="pos-profile-description"
        aria-busy={saving}
      >
        <header className="pos-profile-heading">
          <div>
            <h2 id="pos-profile-title">Actualizar mis datos</h2>

            <p id="pos-profile-description">
              Actualiza tu nombre, correo y contraseña.
            </p>
          </div>

          <button
            type="button"
            className="pos-profile-close"
            onClick={onClose}
            disabled={saving || Boolean(pendingSessionRef.current)}
            aria-label="Cerrar ventana"
          >
            ×
          </button>
        </header>

        {pendingSessionRef.current ? (
          <>
            <p className="pos-profile-error" role="alert">
              {error}
            </p>

            <button
              type="button"
              className="pos-profile-save"
              onClick={recoverSession}
            >
              Reintentar guardar sesión
            </button>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <fieldset disabled={saving}>
              <div className="pos-profile-field">
                <label htmlFor="pos-profile-name">Nombres</label>

                <input
                  ref={nameRef}
                  id="pos-profile-name"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={150}
                  autoComplete="name"
                  required
                />
              </div>

              <div className="pos-profile-field">
                <label htmlFor="pos-profile-email">
                  Usuario / correo electrónico
                </label>

                <input
                  id="pos-profile-email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                />
              </div>

              <PasswordField
                id="pos-profile-current-password"
                label="Contraseña actual"
                value={currentPassword}
                onChange={(event) =>
                  setCurrentPassword(event.target.value)
                }
                disabled={saving}
                required
                autoComplete="current-password"
              />

              <p className="pos-profile-help">
                Deja los siguientes dos campos vacíos para conservar
                tu contraseña actual.
              </p>

              <PasswordField
                id="pos-profile-new-password"
                label="Nueva contraseña"
                value={newPassword}
                onChange={(event) =>
                  setNewPassword(event.target.value)
                }
                disabled={saving}
                autoComplete="new-password"
              />

              <PasswordField
                id="pos-profile-confirm-password"
                label="Confirmar nueva contraseña"
                value={confirmPassword}
                onChange={(event) =>
                  setConfirmPassword(event.target.value)
                }
                disabled={saving}
                autoComplete="new-password"
              />

              {wantsPasswordChange && (
                <div
                  className={
                    passwordsMatch
                      ? "pos-profile-match"
                      : "pos-profile-error"
                  }
                  role="status"
                  aria-live="polite"
                >
                  {passwordsMatch
                    ? "Las contraseñas coinciden."
                    : "Las contraseñas no coinciden."}

                  {newPassword.length < 8 && (
                    <div>Mínimo 8 caracteres.</div>
                  )}

                  {passwordBytes > 72 && (
                    <div>La contraseña supera el límite de 72 bytes.</div>
                  )}
                </div>
              )}

              {error && (
                <p className="pos-profile-error" role="alert">
                  {error}
                </p>
              )}

              <footer className="pos-profile-actions">
                <button
                  type="button"
                  className="pos-profile-cancel"
                  onClick={onClose}
                >
                  Cancelar
                </button>

                <button
                  type="submit"
                  className="pos-profile-save"
                  disabled={saving || invalidNewPassword}
                >
                  {saving ? "Guardando…" : "Guardar cambios"}
                </button>
              </footer>
            </fieldset>
          </form>
        )}
      </section>
    </div>,
    document.body
  );
}